import {
  Button,
  Col,
  DatePicker,
  Drawer,
  Form,
  InputNumber,
  Row,
  Select,
  Space,
  Table,
} from "antd";
import { useEffect, useState } from "react";
import {
  createStaffSalaryInfo,
  getStaffSalaryInfo,
  updateStaffSalaryInfo,
} from "../../../core/store/reducers/staff.slice";
import { useDispatch, useSelector } from "react-redux";
import {
  accessAllowed,
  decryptCTC,
  encryptCTC,
  formatAmount,
  formatDate,
  showNotification,
} from "../../../shared/utils/utils";
import {
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import moment from "moment";
import { requestGetByModuleCommon } from "../../../core/store/reducers/master.slice";
import { getUsersDetails } from "../../../core/store/reducers/auth.slice";
import { RootState } from "../../../shared/types/common";
import { allPermissions } from "../../../shared/constants/constants";

interface Props {
  onClose: () => void;
  visible: boolean;
  selectedEntity?: any;
  onSubmit?: () => void;
}

const StaffSalaryRevisionComponent: React.FC<Props> = ({
  onClose,
  visible,
  selectedEntity = null,
  onSubmit = () => {
    return;
  },
}: Props) => {
  const dispatch = useDispatch();
  const [staffSalaryData, setStaffSalaryData] = useState<any[]>([]);
  const [editingKey, setEditingKey] = useState<string>("");
  const [currencies, setCurrencies] = useState<any[]>([]);
  const { myPermissions } = useSelector((state: RootState) => state.auth);
  const [addNew,setAddNew] = useState(false)

  const [form] = Form.useForm();

  const [userCache, setUserCache] = useState<Record<string, any>>({});

  const fetchUserDetails = async (userId: string) => {
    if (!userId) return null;
    if (userCache[userId]) return userCache[userId]; // ✅ use cached

    const response = await dispatch(
      getUsersDetails({ ids: [userId] })
    ).unwrap();
    const user = response?.data?.[0] || null;

    setUserCache((prev) => ({ ...prev, [userId]: user }));
    return user;
  };

  useEffect(() => {
    fetchStaffSalaryInfo();
    fetchCurrencies();
  }, []);

  const fetchCurrencies = async () => {
    try {
      const response = await dispatch(
        requestGetByModuleCommon({ module: "master_currency" })
      ).unwrap();
      // console.log(response.data);
      if (response?.success) {
        setCurrencies(response?.data.items);
      }
    } catch (error) {}
  };

  const fetchStaffSalaryInfo = async () => {
    if (selectedEntity?.staffId) {
      const response = await dispatch(
        getStaffSalaryInfo(selectedEntity?.staffId)
      ).unwrap();
      if (response?.data) {
        let _data = response?.data;
        let _staffSalaryData = [];
        for (let k = 0; k < _data?.length; ++k) {
          _staffSalaryData.push({
            ..._data[k],
            ctc: _data[k].encrypted_ctc
              ? await decryptCTC(
                  moment(_data[k].updated_date).format("YYYY-MM-DD"),
                  _data[k].encrypted_ctc,
                  _data[k].iv,
                  _data[k].salt
                )
              : _data[k].ctc,
            key: k.toString(),
          });
        }
        setStaffSalaryData(_staffSalaryData);
      }
    }
  };

  const isEditing = (record: any) => record.key === editingKey;

  const edit = async (record: any) => {
    form.setFieldsValue({
      startDate: record.startDate ? moment(record.startDate) : null,
      end_date: record.end_date ? moment(record.end_date) : null,
      salaryCurrency: record.salaryCurrency?.id,
      ctc: record?.ctc,
    });
    setEditingKey(record.key);
  };

  const cancel = () => {
    setEditingKey("");
    fetchStaffSalaryInfo();
    setAddNew(false)
  };

  const save = async (record: any) => {
    const row = await form.validateFields();
    console.log(row, "row", record);
    const payload = {
      staffId: selectedEntity?.staffId,
      startDate: row?.startDate ? new Date(row?.startDate).toISOString() : null,
      endDate: row?.end_date ? new Date(row?.end_date).toISOString() : null,
      salaryCurrency: row?.salaryCurrency,
      // ctc: row?.ctc,
      ...(await encryptCTC(
        moment().format("YYYY-MM-DD"),
        row?.ctc?.toString()
      )),
      // employeeCost: parseFloat((selectedEntity?.ctc / 12).toFixed(2)),
    };
    if (record?.id) {
      const response = await dispatch(
        updateStaffSalaryInfo({ id: record?.id, body: payload })
      ).unwrap();
      if (response?.success) {
        showNotification(
          "Staff Salary Revision Updated Successfully",
          "success"
        );
        fetchStaffSalaryInfo();
        setEditingKey("");
      } else {
        showNotification(response?.message, "error");
      }
      console.log(response);
    } else {
      const response = await dispatch(createStaffSalaryInfo(payload)).unwrap();
      if (response?.success) {
        showNotification(
          "Staff Salary Revision Created Successfully",
          "success"
        );
        fetchStaffSalaryInfo();
        setEditingKey("");
      } else {
        showNotification(response?.message, "error");
      }
    }
    setAddNew(false);
    console.log(payload, "payload");
  };

  const columns: any = [
    {
      title: "Revision No",
      key: "index",
      align: "center",
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: "Revision Date",
      dataIndex: "updated_date",
      key: "updated_date",
      align: "center",
      render: (date: string) => (date ? formatDate(date, "DD-MMM-YYYY") : ""),
    },
    {
      title: "Revised By",
      dataIndex: "revisedBy",
      key: "revisedBy",
      align: "center",
      render: (_: any, record: any) => {
        const userId = record.updated_by || record.created_by;

        if (!userId) return "";

        // If user already cached, show immediately
        if (userCache[userId]) {
          return userCache[userId].name || userCache[userId].fullName || "";
        }

        // Trigger fetch and show loading while waiting
        fetchUserDetails(userId);
      },
    },

    {
      title: "Contract Start Date",
      dataIndex: "startDate",
      key: "startDate",
      align: "center",
      render: (_: any, record: any) =>
        isEditing(record) ? (
          <Form.Item
            name="startDate"
            rules={[{ required: true }]}
            style={{ margin: 0 }}
            initialValue={record.startDate ? dayjs(record.startDate) : null}
          >
            <DatePicker format="DD-MMM-YYYY" />
          </Form.Item>
        ) : record.startDate ? (
          formatDate(record.startDate, "DD-MMM-YYYY")
        ) : (
          ""
        ),
    },
    {
      title: "Contract End Date",
      dataIndex: "end_date",
      key: "end_date",
      align: "center",
      render: (_: any, record: any) =>
        isEditing(record) ? (
          <Form.Item
            name="end_date"
            rules={[{ required: false }]}
            style={{ margin: 0 }}
            initialValue={record.end_date ? dayjs(record.end_date) : null}
          >
            <DatePicker
              format="DD-MMM-YYYY"
              disabledDate={(current) => {
                // get the selected start date from form
                const startDate = form.getFieldValue("startDate");
                if (!startDate) return false; // if no start date selected, don't disable anything
                return current && current < startDate.startOf("day");
              }}
            />
          </Form.Item>
        ) : record.end_date ? (
          formatDate(record.end_date, "DD-MMM-YYYY")
        ) : (
          ""
        ),
    },

    {
      title: "Currency",
      dataIndex: "currency",
      key: "currency",
      align: "center",
      render: (_: any, record: any) =>
        isEditing(record) ? (
          <Form.Item
            name="salaryCurrency"
            rules={[{ required: true }]}
            style={{ margin: 0 }}
          >
            <Select
              style={{ width: 120 }}
              options={currencies?.map((Currency) => ({
                value: Currency?.id,
                label: Currency?.name,
              }))}
            />
          </Form.Item>
        ) : (
          record.salaryCurrency?.name || ""
        ),
    },
    {
      title: "CTC",
      dataIndex: "ctc",
      key: "ctc",
      align: "center",
      render: (_: any, record: any) =>
        isEditing(record) ? (
          <Form.Item
            name="ctc"
            rules={[{ required: true }]}
            style={{ margin: 0 }}
            initialValue={0}
          >
            <InputNumber
              style={{ width: 120 }}
              step={0.01}
              precision={2}
              formatter={(value) => {
                if (!value && value !== 0) return "";
                return new Intl.NumberFormat("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }).format(Number(value));
              }}
              parser={(value) => {
                if (!value) return "";
                // Remove commas and parse back to number
                const cleaned = value.replace(/,/g, "");
                return parseFloat(cleaned);
              }}
            />
          </Form.Item>
        ) : record.ctc ? (
          formatAmount(record.ctc)
        ) : (
          ""
        ),
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      render: (_: any, record: any) => {
        const editable = isEditing(record);
        const hasAccess = accessAllowed(
          myPermissions,
          allPermissions.UPDATE_STAFF_SALARY_REVISION
        );
    
        return editable ? (
          <Space>
            <Button
              type="link"
              icon={<SaveOutlined />}
              onClick={() => save(record)}
            />
            <Button type="link" icon={<CloseOutlined />} onClick={cancel} />
          </Space>
        ) : (
          <Button
            type="link"
            icon={<EditOutlined />}
            disabled={!hasAccess || (editingKey !== "" && editingKey !== record.key)}
            onClick={() => edit(record)}
          />
        );
      },
    }
    
  ];

  const onDrawerClose = () => {
    onClose();
    setStaffSalaryData([]);
  };

  const handleAddNewRow = () => {
    setAddNew(true);
    const newKey = (Math.max(0, ...staffSalaryData.map(d => parseInt(d.key))) + 1).toString();;

    const newRow = {
      key: newKey,
      startDate: null,
      end_date: null,
      salaryCurrency: { id: "", name: "" },
      ctc: null,
      revisedBy: "",
      updated_date: formatDate(new Date(), "YYYY-MM-DD"),
    };

    setStaffSalaryData([newRow, ...staffSalaryData]);
    setEditingKey(newKey);

    // 👇 Initialize the form fields for this new row
    form.setFieldsValue({
      startDate: null,
      end_date: null,
      salaryCurrency: undefined,
      ctc: undefined,
    });
  };

  const onValuesChange = (changedValues: any, allValues: any) => {
    if (changedValues.startDate) {
      form.setFieldsValue({ end_date: null });
    }
  };

  return (
    <Drawer
      open={visible}
      onClose={onDrawerClose}
      title="Staff Salary Revision"
      width={"90%"}
    >
      <Row>
        <Col span={12}>Staff Name: {selectedEntity?.staffName}</Col>
        <Col span={12}>Employee Id: {selectedEntity?.employeeId}</Col>
      </Row>
      <div className="mt-5">
        <Form form={form} component={false} onValuesChange={onValuesChange}>
          <Table
            dataSource={staffSalaryData}
            columns={columns}
            pagination={false}
            bordered
          />
        </Form>
      </div>
      <Row justify={"end"}>
        {accessAllowed(
          myPermissions,
          allPermissions.CREATE_STAFF_SALARY_REVISION
        ) && (
          <Button
            type="primary"
            shape="circle"
            icon={<PlusOutlined />}
            className="mt-3"
            onClick={handleAddNewRow}
            disabled={addNew}
          />
        )}
      </Row>
    </Drawer>
  );
};

export default StaffSalaryRevisionComponent;
